import { catchError, map, Observable, of, startWith } from 'rxjs';

export type AsyncState<T> = { status: 'loading' } | { status: 'success'; data: T } | { status: 'error'; error: string };

export const loading = <T>(): AsyncState<T> => ({ status: 'loading' });
export const success = <T>(data: T): AsyncState<T> => ({
  status: 'success',
  data,
});
export const error = <T>(message: string): AsyncState<T> => ({
  status: 'error',
  error: message,
});

export function isLoading<T>(state: AsyncState<T>): state is { status: 'loading' } {
  return state.status === 'loading';
}

export function isSuccess<T>(state: AsyncState<T>): state is { status: 'success'; data: T } {
  return state.status === 'success';
}

export function isError<T>(state: AsyncState<T>): state is { status: 'error'; error: string } {
  return state.status === 'error';
}

export function getData<T>(state: AsyncState<T>): T | undefined {
  return state.status === 'success' ? state.data : undefined;
}

export function getError<T>(state: AsyncState<T>): string | undefined {
  return state.status === 'error' ? state.error : undefined;
}

export function toAsyncState<T>() {
  return (source$: Observable<T>): Observable<AsyncState<T>> =>
    source$.pipe(
      map(data => success(data)),
      startWith(loading<T>()),
      catchError(err => of(error<T>(err?.message ?? 'An unexpected error occurred'))),
    );
}
